<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'
import { useAiChatStore } from '@/stores/aiChat'
import { Send, Bot, User, Wrench, ChevronDown, ChevronRight, Loader2 } from 'lucide-vue-next'
import { marked } from 'marked'
import { useSanitize } from '@/composables/useSanitize'

const { sanitize } = useSanitize()

marked.setOptions({
  breaks: true,
  gfm: true,
})

function renderMarkdown(content: string): string {
  if (!content) return ''
  const raw = marked.parse(content, { async: false }) as string
  return sanitize(raw)
}

const aiStore = useAiChatStore()
const input = ref('')
const messagesContainer = ref<HTMLElement | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const expandedTools = ref<Set<string>>(new Set())

function autoResize() {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function resetTextarea() {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
}

function toggleTool(msgId: string) {
  if (expandedTools.value.has(msgId)) {
    expandedTools.value.delete(msgId)
  } else {
    expandedTools.value.add(msgId)
  }
}

async function handleSend() {
  const msg = input.value.trim()
  if (!msg || aiStore.isSending) return

  input.value = ''
  resetTextarea()

  if (!aiStore.selectedConversationId) {
    await aiStore.startConversation(msg)
  } else {
    await aiStore.sendMessage(msg)
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

watch(() => aiStore.messages.length, scrollToBottom)
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Messages -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto p-3 space-y-3">
      <!-- Empty state -->
      <div v-if="!aiStore.selectedConversationId && aiStore.messages.length === 0" class="flex flex-col items-center justify-center h-full text-center px-4">
        <Bot class="w-10 h-10 text-violet-400 mb-3" />
        <p class="text-sm font-medium text-surface-200 mb-1">Ask Vigilante</p>
        <p class="text-xs text-surface-400">Search tickets, troubleshoot devices, and query the knowledge base — all in one place.</p>
      </div>

      <!-- Message bubbles -->
      <div
        v-for="msg in aiStore.messages"
        :key="msg.id"
        class="flex"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          class="max-w-[85%] rounded-lg px-3 py-2 text-sm"
          :class="{
            'bg-violet-600 text-white': msg.role === 'user',
            'bg-surface-700 text-surface-100': msg.role === 'assistant' && !msg.isError,
            'bg-danger-900/20 text-surface-100 border-l-4 border-danger-500': msg.role === 'assistant' && msg.isError,
            'bg-surface-800 text-surface-300 text-xs border border-surface-700': msg.role === 'tool',
          }"
        >
          <!-- Role icon -->
          <div class="flex items-start gap-2">
            <User v-if="msg.role === 'user'" class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-70" />
            <Bot v-else-if="msg.role === 'assistant'" class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-violet-400" />

            <div class="min-w-0">
              <!-- Message content with markdown rendering -->
              <div
                v-if="msg.role === 'assistant'"
                class="prose prose-sm prose-invert max-w-none break-words overflow-x-auto
                       prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
                       prose-headings:text-surface-100 prose-headings:mt-2 prose-headings:mb-1
                       prose-strong:text-surface-100 prose-code:text-violet-300
                       prose-table:text-xs prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1
                       prose-th:bg-surface-600 prose-th:text-surface-200
                       prose-td:border-surface-600"
                v-html="renderMarkdown(msg.content)"
              />
              <div v-else class="whitespace-pre-wrap break-words" v-text="msg.content" />

              <!-- Tool calls accordion -->
              <div v-if="msg.tool_calls && msg.tool_calls.length > 0" class="mt-2 border-t border-surface-600 pt-2">
                <button
                  @click="toggleTool(msg.id)"
                  class="flex items-center gap-1 text-xs text-surface-400 hover:text-surface-200 transition-colors"
                >
                  <component :is="expandedTools.has(msg.id) ? ChevronDown : ChevronRight" class="w-3 h-3" />
                  <Wrench class="w-3 h-3" />
                  {{ msg.tool_calls.length }} tool{{ msg.tool_calls.length > 1 ? 's' : '' }} used
                </button>
                <div v-if="expandedTools.has(msg.id)" class="mt-1 space-y-1">
                  <div
                    v-for="(tc, idx) in msg.tool_calls"
                    :key="idx"
                    class="text-xs bg-surface-800 rounded px-2 py-1"
                  >
                    <span class="font-mono text-violet-300">{{ tc.name }}</span>
                    <span class="text-surface-500 ml-1">{{ tc.result_summary }}</span>
                  </div>
                </div>
              </div>

              <!-- Response time -->
              <div v-if="msg.role === 'assistant' && msg.response_time_ms" class="mt-1 text-xs text-surface-500">
                {{ (msg.response_time_ms / 1000).toFixed(1) }}s
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Typing indicator -->
      <div v-if="aiStore.isSending" class="flex justify-start">
        <div class="bg-surface-700 rounded-lg px-3 py-2 text-sm flex items-center gap-2">
          <Loader2 class="w-3.5 h-3.5 text-violet-400 animate-spin" />
          <span class="text-surface-400 text-xs">Vigilante is thinking...</span>
        </div>
      </div>
    </div>

    <!-- Input area -->
    <div class="border-t border-surface-700 p-3">
      <div class="flex items-end gap-2">
        <textarea
          ref="textareaRef"
          v-model="input"
          @keydown="handleKeydown"
          @input="autoResize"
          placeholder="Ask Vigilante anything..."
          rows="1"
          class="flex-1 resize-none bg-surface-800 border border-surface-600 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:border-violet-500 max-h-32 overflow-y-auto"
        />
        <button
          @click="handleSend"
          :disabled="!input.trim() || aiStore.isSending"
          class="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
        >
          <Send class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>
